import type { ParsedPrompt, ChatMessage } from "./types/parser";
import { validateAIResponse } from "./promptValidator";
import { ConversationMemory } from "./ConversationMemory";
import { supabase } from "@/lib/supabase";

export class PromptParser {
  private conversationMemory: ConversationMemory;
  private readonly MAX_RETRIES = 3;
  private retryCount = 0;
  private readonly RETRY_DELAY = 1000;

  constructor() {
    this.conversationMemory = new ConversationMemory(50);
  }

  private async sleep(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  async parse(input: string): Promise<ParsedPrompt> {
    try {
      console.log('Starting parse with input:', input);
      
      const userMessage: ChatMessage = {
        role: 'user',
        content: input,
        timestamp: Date.now()
      };
      this.conversationMemory.add(userMessage);

      const context = this.conversationMemory.getAll().map(msg => ({
        role: msg.role,
        content: msg.content,
        metadata: msg.metadata
      }));

      console.log('Making API call with context:', JSON.stringify(context, null, 2));
      const { data, error } = await this.makeAPICallWithRetry(input, context);

      if (error) {
        console.error('Final error after retries:', error);
        return this.handleError();
      }

      console.log('Raw API response:', JSON.stringify(data, null, 2));
      
      this.retryCount = 0;
      const validated = validateAIResponse(data, this.conversationMemory.getState());
      
      if (!validated) {
        console.error('Failed to validate AI response');
        return this.handleInvalidResponse();
      }

      console.log('Validated AI response:', JSON.stringify(validated, null, 2));

      let parsedDateTime = null;
      if (validated.dateTime) {
        try {
          parsedDateTime = new Date(validated.dateTime);
          console.log('Parsed date:', parsedDateTime);
        } catch (error) {
          console.error('DateTime parsing error:', error);
          parsedDateTime = null;
        }
      }

      const aiMessage: ChatMessage = {
        role: 'assistant',
        content: validated.feedback,
        timestamp: Date.now(),
        metadata: {
          dateTime: parsedDateTime?.toISOString(),
          attendeeName: validated.attendeeName,
          action: validated.action,
          isComplete: validated.isComplete
        }
      };
      
      console.log('Created AI message:', JSON.stringify(aiMessage, null, 2));
      this.conversationMemory.add(aiMessage);

      const result: ParsedPrompt = {
        type: validated.type,
        isComplete: validated.isComplete,
        attendeeName: validated.attendeeName?.toLowerCase(),
        dateTime: parsedDateTime,
        duration: validated.duration || 30,
        feedback: validated.feedback
      };
      
      console.log('Final parsed result:', JSON.stringify(result, null, 2));
      return result;

    } catch (error) {
      console.error('Unexpected error in prompt parser:', error);
      return this.handleError();
    }
  }

  private async makeAPICallWithRetry(input: string, context: any[]) {
    for (let attempt = 0; attempt <= this.MAX_RETRIES; attempt++) {
      if (attempt > 0) {
        console.log(`Retry attempt ${attempt}/${this.MAX_RETRIES}`);
        await this.sleep(this.RETRY_DELAY);
      }

      console.log('Making API call with:', { input, context });
      const response = await supabase.functions.invoke('parse-meeting-chat', {
        body: {
          userInput: input,
          context,
          state: this.conversationMemory.getState()
        }
      });

      console.log('API response:', JSON.stringify(response, null, 2));

      if (!response.error) {
        return response;
      }

      console.error(`API call attempt ${attempt + 1} failed:`, response.error);
    }

    return { data: null, error: new Error('Max retries exceeded') };
  }

  private handleError(): ParsedPrompt {
    const errorMessage = "I'm having trouble understanding. Could you rephrase that?";
    
    const errorResponse: ChatMessage = {
      role: 'assistant',
      content: errorMessage,
      timestamp: Date.now(),
      metadata: {
        isComplete: false
      }
    };
    this.conversationMemory.add(errorResponse);

    return {
      type: 'meeting_request',
      isComplete: false,
      dateTime: null,
      duration: 30,
      feedback: errorMessage
    };
  }

  private handleInvalidResponse(): ParsedPrompt {
    const errorMessage = "I didn't quite catch that. Could you provide more details about when you'd like to meet?";
    
    const errorResponse: ChatMessage = {
      role: 'assistant',
      content: errorMessage,
      timestamp: Date.now(),
      metadata: {
        isComplete: false
      }
    };
    this.conversationMemory.add(errorResponse);

    return {
      type: 'meeting_request',
      isComplete: false,
      dateTime: null,
      duration: 30,
      feedback: errorMessage
    };
  }

  clearMemory() {
    console.log('Clearing conversation memory');
    this.conversationMemory.clear();
    this.retryCount = 0;
  }
}

export const parser = new PromptParser();

export const parsePrompt = async (input: string): Promise<ParsedPrompt> => {
  return parser.parse(input);
};
